import styles from "../App.module.scss";
import Grid from "@mui/material/Grid";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {selectTags} from "../store/selectors";
import {Box, IconButton} from "@mui/material";
import {PlusOne, Save} from "@mui/icons-material";
import Button from "@mui/material/Button";
import {Tag, TagCategory} from "../gen/api";
import {AppDispatch} from "../store";
import {updateTags} from "../store/reducers/tags";
import AddGroupDialog from "../components/AddGroupDialog/AddGroupDialog";
import {v4 as uuid} from "uuid";
import Groups from "../components/EditTags/Groups";
import SecondaryHeader from "../components/Header/SecondaryHeader";
import {useNavigate} from "react-router-dom";
import GridPaper from "../components/ContentBox/GridPaper";

export default function EditGroups() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [tags, setTags] = useState<Partial<TagCategory>[]>([]);
  const [parent, setParent] = useState<Partial<TagCategory> | Partial<Tag>>();
  const persistedTags = useSelector(selectTags);
  const [isAddGroupModalOpen, setAddGroupModalOpen] = useState(false);

  useEffect(() => {
    if (tags.length === 0) setTags(persistedTags);
  }, [persistedTags, tags.length]);

  const onSubmit = (tags: Partial<TagCategory>[]) => dispatch(updateTags(tags));

  const onChange = (tag: Tag, event: React.ChangeEvent<HTMLInputElement>) => setTags(tags.map(t => ({
    ...t,
    tags: changeTag(t.tags, tag.id!, event.target.value) ?? []
  })));

  const onDelete = (tag: Tag) => setTags(tags.map(t => ({...t, tags: removeTag(t.tags, tag.id!)})));

  const onAddGroupModalClose = () => setAddGroupModalOpen(false);

  const onAddNew = (target: Partial<TagCategory> | Partial<Tag>) => {
    setParent(target);
    setAddGroupModalOpen(true);
  };

  const addTag = (tags: Tag[] | undefined, id: string, value: string): Tag[] | undefined => {
    if (!tags) return undefined;
    return tags.map(tag => {
      if (tag.id === id) {
        if (tag.childTags) {
          return {...tag, childTags: [...tag.childTags, {id: uuid(), name: value, childTags: []}]};
        } else {
          return {...tag, childTags: [{id: uuid(), name: value, childTags: []}]};
        }
      } else {
        return {...tag, childTags: addTag(tag.childTags, id, value) ?? []}
      }
    });
  }

  const changeTag = (tags: Tag[] | undefined, id: string, value: string): Tag[] | undefined => {
    if (!tags) return undefined;
    return tags
      .map(tag => tag.id === id ? {...tag, name: value} : {
        ...tag,
        childTags: changeTag(tag.childTags, id, value) ?? []
      });
  }

  const removeTag = (tags: Tag[] | undefined, id: string): Tag[] | undefined => {
    if (!tags) return undefined;
    return tags
      .map(tag => tag.childTags ? {...tag, childTags: removeTag(tag.childTags, id)} : tag)
      .filter(tag => tag.id !== id);
  }

  const addNewGroup = (target: Partial<TagCategory> | Partial<Tag>, name: string) => {
    // add to tags if target is a cluster or add to childTags if target is a group
    if (target.hasOwnProperty('tags')) {
      setTags(tags.map(t => t.id === target.id ? {
        ...t,
        tags: [...t.tags!, {id: uuid(), name: name, childTags: []}]
      } : t));
    } else if (target.hasOwnProperty('childTags')) {
      setTags(tags.map(t => ({...t, tags: addTag(t.tags, target.id!, name)})));
    }
    onAddGroupModalClose();
  }

  return (
    <div className={styles.main}>
      <Grid container spacing={1}>
        <SecondaryHeader>
          <Grid item flex={1} alignSelf="center">
            <Button variant="contained" onClick={() => navigate(-1)}>Zurück</Button>
          </Grid>
          <Grid item flex={1} textAlign="center">
            <h1 className={styles['content-title']}>Gruppen bearbeiten</h1>
          </Grid>
          <Grid item flex={1} alignSelf="center" textAlign="end">
            {tags.length > 0 && <Button variant="contained"
                                        endIcon={<Save/>}
                                        onClick={() => onSubmit(tags)}
                                        disabled={tags === persistedTags}>
                Änderungen speichern
            </Button>}
          </Grid>
        </SecondaryHeader>
        <GridPaper>
          {tags.map(cluster =>
            <>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <h3>{cluster.name}</h3>
                <IconButton onClick={() => onAddNew(cluster)}><PlusOne/></IconButton>
              </Box>
              {cluster.tags &&
                  <Groups data={cluster.tags} levels={1}
                          onChange={onChange} onDelete={onDelete} onAddNew={onAddNew}/>
              }
            </>
          )}
        </GridPaper>
      </Grid>
      {parent &&
          <AddGroupDialog isOpen={isAddGroupModalOpen}
                          onClose={onAddGroupModalClose}
                          onSubmit={addNewGroup}
                          parent={parent}/>
      }
    </div>
  );
}